import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Children, useEffect, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import { ClickOutside } from 'Components/frameworks/click-outside/ClickOutside';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Modal } from 'Components/frameworks/modal/Modal';
import { themes } from './PopOver.themes';
type Props = {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  containerClassName?: string;
  content: ReactNode;
  hasCloseButton?: boolean;
  isInitiallyVisible?: boolean;
  isModalOnly?: boolean;
  onClick?: (e?: React.SyntheticEvent<any>) => void;
  onRender?: () => void;
  onTimeout?: () => void;
  theme?: ThemeKey<typeof themes>;
  timeoutInMs?: number;
  triggerOnHover?: boolean;
};
export const PopOver = ({
  ariaLabel,
  children,
  className,
  contentClassName = '',
  containerClassName = '',
  content,
  hasCloseButton = false,
  isInitiallyVisible = false,
  isModalOnly = false,
  onClick,
  onRender,
  onTimeout,
  theme = 'default',
  timeoutInMs,
  triggerOnHover,
}: Props) => {
  const [isVisible, setIsVisible] = useState(isInitiallyVisible);
  const { isDesktop } = useResponsive();
  useEffect(() => {
    onRender && onRender();

    if (timeoutInMs && timeoutInMs > 0) {
      setTimeout(() => {
        setIsVisible(false);
        onTimeout && onTimeout();
      }, timeoutInMs);
    }

    return () => {
      onTimeout && onTimeout();
    };
  }, []);
  const shouldTriggerOnHover = triggerOnHover && isDesktop;
  useEffect(() => {
    let timeoutId;

    if (shouldTriggerOnHover && isVisible && onClick) {
      timeoutId = setTimeout(onClick, 300);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isVisible, shouldTriggerOnHover]);

  if (!Children.count(children)) {
    return null;
  }

  const { thTrigger, thContainer, thContent, thCloseButton, thCloseIconWidth } = themes[
    theme
  ] as DefaultThemeStyles<typeof themes>;
  return (
    <div
      className={classnames('relative isolate', className)}
      {...(shouldTriggerOnHover && {
        onMouseEnter: (e) => {
          setIsVisible(true);

          if (!shouldTriggerOnHover) {
            onClick?.(e);
          }
        },
        onMouseLeave: () => {
          setIsVisible(false);
        },
      })}
    >
      <button
        className={thTrigger}
        data-testid="PopOver-button"
        onClick={(e) => {
          e.preventDefault();

          if (!timeoutInMs) {
            setIsVisible(!isVisible);
          }

          if (!isVisible && !shouldTriggerOnHover && onClick) {
            onClick(e);
          }
        }}
        type="button"
      >
        {children}
      </button>
      {isVisible &&
        (isDesktop && !isModalOnly ? (
          <ClickOutside
            className={classnames(thContainer, containerClassName)}
            onClick={() => {
              setIsVisible(false);
            }}
          >
            <div className={classnames(thContent, contentClassName)}>
              {hasCloseButton && (
                <button className={thCloseButton} onClick={() => setIsVisible(false)} type="button">
                  <Icon name={ICONS.CROSS} width={thCloseIconWidth} />
                </button>
              )}
              {content}
            </div>
          </ClickOutside>
        ) : (
          <Modal
            ariaLabel={ariaLabel}
            closeLabel={t('common.close')}
            onClose={(e) => {
              e.preventDefault();
              setIsVisible(false);
            }}
            size="small"
          >
            {content}
          </Modal>
        ))}
    </div>
  );
};
